import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Menu, Layout } from 'antd'
import { useHistory } from 'react-router-dom'
import useReactRouter from 'use-react-router'
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons'

import { ReactComponent as DashboardIcon } from '../../assets/icons/dashboard.svg'
import { ReactComponent as ProjectsIcon } from '../../assets/icons/projects.svg'
import { ReactComponent as UserIcon } from '../../assets/icons/user.svg'
import { ReactComponent as Company } from '../../assets/icons/company.svg'
import { ReactComponent as Activity } from '../../assets/icons/activity.svg'
import { ReactComponent as TodoList } from '../../assets/icons/todo.svg'
import { ReactComponent as Techstack } from '../../assets/icons/techstack.svg'
import { ReactComponent as SalesPersons } from '../../assets/icons/salesPersons.svg'
import { ReactComponent as RevenueIcon } from '../../assets/icons/revenue.svg'
import { ReactComponent as RateMasterIcon } from '../../assets/icons/rateMaster.svg'

import './Sidebar.scss'

import { getSideBarOptions } from '../../utils/helperFunctions/helperFunctions'
import { AuthContext } from '../../store/auth'
import { UserRoles } from '../../utils/constants/constant'

const { Sider } = Layout

const sideBarOptions = [
  {
    icon: DashboardIcon,
    key: '/invoiceDashboard',
    label: 'Invoice Dashboard',
    path: '/invoiceDashboard',
    accountant: true,
    csm: true,
    pm: false,
  },
  {
    icon: RateMasterIcon,
    key: '/rateMaster',
    label: 'Rate Master',
    path: '/rateMaster',
    accountant: true,
    csm: true,
    pm: false,
  },
  {
    icon: ProjectsIcon,
    key: '/projectlist',
    label: 'Projects',
    path: '/projectlist',
    accountant: true,
    csm: true,
    pm: false,
  },
  {
    icon: RevenueIcon,
    key: '/revenuelist',
    label: 'Revenue',
    path: '/revenuelist',
    accountant: true,
    csm: true,
    pm: false,
  },
  {
    icon: Company,
    key: '/companylist',
    label: 'Company',
    path: '/companylist',
    accountant: true,
    csm: true,
    pm: false,
  },
  {
    icon: SalesPersons,
    key: '/salesPersons',
    label: 'Sales Persons',
    path: '/salesPersons',
    accountant: false,
    csm: false,
    pm: false,
  },
  {
    icon: TodoList,
    key: '/todolist',
    label: 'To-Do',
    path: '/todolist',
    accountant: true,
    csm: true,
    pm: false,
  },
  {
    icon: Techstack,
    key: '/techstack',
    label: 'Techstack',
    path: '/techstack',
    accountant: false,
    csm: false,
    pm: false,
  },
  {
    icon: Activity,
    key: '/useractivity',
    label: 'Activity',
    path: '/useractivity',
    accountant: true,
    csm: true,
    pm: false,
  },
  {
    icon: UserIcon,
    key: '/userlist',
    label: 'User',
    path: '/userlist',
    accountant: false,
    csm: false,
    pm: false,
  },
  {
    icon: ProjectsIcon,
    key: '/projectdelivery',
    label: 'Project Delivery',
    path: '/projectdelivery',
    // A project row click from this list (and the Anomaly Dashboard's action
    // links) lands PM on one of these project sub-page routes directly --
    // none of them start with `/projectdelivery` itself, so without also
    // matching these prefixes the sidebar loses its highlight the moment a
    // PM drills into a project (see [[known-issues.md]]).
    matchPaths: ['/deliveryprogress', '/estimationhistory', '/developers'],
    accountant: false,
    csm: false,
    pm: true,
    // ROLE_ADMIN otherwise sees every sidebar item unfiltered (see
    // getSideBarOptions's default branch) -- Project Delivery is PM-only by
    // design, so it opts out of that default via this explicit flag.
    admin: false,
  },
  {
    icon: Activity,
    key: '/anomalies',
    label: 'Anomalies',
    path: '/anomalies',
    accountant: true,
    csm: true,
    pm: false,
  },
]

const Sidebar = () => {
  const history = useHistory()
  const { location } = useReactRouter()
  const currentSelectedItem = location.state?.sidebarSelected
  const { userRole } = useContext(AuthContext)
  // Initialize state from localStorage or default to false
  const [collapsed, setCollapsed] = useState(() => {
    const storedCollapsed = localStorage.getItem('sidebarCollapsed')
    return storedCollapsed ? JSON.parse(storedCollapsed) : false
  })

  // Update localStorage and body class when collapsed state changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed))
    if (collapsed) {
      document.body.classList.add('sidebar-collapsed')
    } else {
      document.body.classList.remove('sidebar-collapsed')
    }
    return () => {
      document.body.classList.remove('sidebar-collapsed')
    }
  }, [collapsed])

  function onMenuItemClick({ item, key, keyPath }) {
    history.push({
      pathname: item.props.path,
      state: { sidebarSelected: key },
    })
  }

  const toggleCollapsed = () => setCollapsed(!collapsed)

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggleCollapsed()
    }
  }

  const pathname = location?.pathname
  const selectedKeys = useMemo(() => {
    if (
      pathname === '/settings/jobScheduler' ||
      pathname === '/settings/serverLogs' ||
      pathname === '/settings/identityProvider'
    ) {
      return []
    }

    // `matchPaths` (Project Delivery's project sub-pages) is only safe to
    // match for ROLE_PM: `/developers/:id`/`/projectdetails/:id` are also
    // reached via Admin/CSM/Accountant's own "Projects" list flow, and
    // ROLE_ADMIN's sidebar shows every item unfiltered (`getSideBarOptions`'s
    // default branch), so matching `matchPaths` for those roles could steal
    // the highlight from "Projects" onto "Project Delivery". PM has no such
    // ambiguity -- `pmAccessibleRoutesArray` never includes `projectlist`,
    // so these routes are only ever reached through Project Delivery for PM.
    const matchedItem = sideBarOptions.find((item) => {
      if (pathname?.startsWith(item.path)) return true
      return (
        userRole === UserRoles.PM &&
        item.matchPaths?.some((matchPath) => pathname?.startsWith(matchPath))
      )
    })

    if (matchedItem) {
      return [matchedItem.key]
    }

    return currentSelectedItem ? [currentSelectedItem] : []
  }, [pathname, currentSelectedItem, userRole])

  return (
    <>
      <Sider
        width={220}
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        trigger={null} // We're using our own trigger button
        style={{
          position: 'fixed',
          height: '100vh',
          zIndex: 999,
        }}
      >
        <div
          className="sidebar-trigger"
          role="button"
          tabIndex={0}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={toggleCollapsed}
          onKeyDown={handleTriggerKeyDown}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
        <Menu
          mode="inline"
          onClick={onMenuItemClick}
          selectedKeys={selectedKeys}
          defaultOpenKeys={['sub1']}
          items={getSideBarOptions(userRole, sideBarOptions)}
          inlineCollapsed={collapsed}
        />
      </Sider>
    </>
  )
}

export default Sidebar
